export interface Trader {
	address: string;
	name: string | null;
	x_username: string | null;
	followers: number | null;
	volume_30d: number | null;
	volume_lifetime: number | null;
	pnl_lifetime: number | null;
	unrealized_pnl: number | null;
	roi: number | null;
	win_rate: number | null;
	/** Set by rules over 30 days of behaviour. */
	persona: string | null;
	maker_share: number | null;
	hold_median_h: number | null;
	top_category: string | null;
	unique_markets?: number | null;
	created_at?: string | null;
	/** Set by an LLM on a sample of wallets, so a guess. Feeds nothing else. */
	llm_persona?: string | null;
	llm_confidence?: number | null;
	llm_evidence?: string | null;
}

export interface SegmentsDoc {
	generated_at: string;
	window: { days: number; ending: string };
	top_traders: Trader[];
}

const PERSONA_LABEL: Record<string, string> = {
	bot: "Bot",
	buy_and_hold: "Buy & hold",
	market_maker: "Market maker",
	mixed: "Mixed",
	scalper: "Scalper",
	longshot_gambler: "Longshot",
	swing: "Swing",
	swing_trader: "Swing",
	arbitrageur: "Arbitrage",
	casual_retail: "Casual",
};

export const personaLabel = (p: string | null | undefined): string => (p ? (PERSONA_LABEL[p] ?? p.replaceAll("_", " ")) : "–");

const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** The row for `address`, ignoring case: gamma and the snapshot differ on checksums (pure). */
export function findByAddress<T extends { address: string }>(rows: T[] | undefined, address: string): T | null {
	return rows?.find((r) => same(r.address, address)) ?? null;
}

export function walletDisplayName(address: string, ...names: (string | null | undefined)[]): string | null {
	// Polymarket fills unset names with the address, which is not a name.
	return names.find((n) => n && !/^(0x)?[0-9a-f]{30,}/i.test(n) && !same(n, address)) ?? null;
}

/**
 * Splits what the API calls open positions. A position on a resolved market that is worth
 * nothing is a finished loss waiting to be cleared, so it does not belong in an open list (pure).
 */
export function splitPositions<T extends { redeemable: boolean; current_value: number }>(list: T[]): { open: T[]; spent: number } {
	const open = list.filter((p) => !(p.redeemable && p.current_value <= 0));
	return { open, spent: list.length - open.length };
}

const pctOf = (v: number | null | undefined) => (v === null || v === undefined ? null : `${Math.round(v * 100)}%`);

/** "Bot, 95% sure" for the LLM label, or null when the wallet was not in the sample (pure). */
export function llmGuess(t: Pick<Trader, "llm_persona" | "llm_confidence"> | null): string | null {
	if (!t?.llm_persona) return null;
	const sure = pctOf(t.llm_confidence);
	return sure ? `${personaLabel(t.llm_persona)}, ${sure} sure` : personaLabel(t.llm_persona);
}
